// @owners { team: patients }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { subscriptionsConstants } from '~shared/constants';
import { get, put } from '~shared/helpers/apiHelper';
import getURLParam from '~shared/helpers/getURLParam';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { useMutation, useQuery, useQueryClient } from '~shared/react-query';
import { type Subscription } from '~shared/types';

export type UserSubscriptions = {
  email: string;
  subscriptions: Subscription[];
};

export const userSubscriptions = createQueryKeys('userSubscriptions', {
  fetch: ({ uuid }: { uuid: string }) => ({
    queryKey: [uuid],
    queryFn: async (): Promise<UserSubscriptions> => {
      const response = await get(`/subscriptions/${uuid}`);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      const subscribedCommsTypes: string[] = response.subscriptions ?? [];
      return {
        email: response.email ?? '',
        subscriptions: Object.keys(subscriptionsConstants).map((comms_type) => ({
          comms_type,
          subscribed: subscribedCommsTypes.includes(comms_type),
        })),
      };
    },
  }),
});

/**
 * Fetch the marketing communication subscriptions for a patient by unsubscribe uuid.
 * Used on the unauthenticated subscription management (unsubscribe) page.
 */
export const useUserSubscriptions = ({ uuid }: { uuid: string }) => {
  const { data, error, isPending } = useQuery({
    ...userSubscriptions.fetch({ uuid }),
  });

  return {
    email: data?.email ?? '',
    subscriptions: data?.subscriptions ?? [],
    error,
    isPending,
  };
};

type UpdateUserSubscriptionsParams = {
  uuid: string;
  subscriptions: Subscription[];
  unSubscribeAll?: boolean;
};

const UNSUBSCRIBE_ALL_COMMS: Subscription = {
  comms_type: 'all_comms',
  subscribed: false,
};

/**
 * Update the marketing communication subscriptions for a patient by unsubscribe uuid.
 * Attribution params come from the URL the patient followed out of an email.
 */
export const useUpdateUserSubscriptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ uuid, subscriptions, unSubscribeAll = false }: UpdateUserSubscriptionsParams) => {
      const params = {
        subscriptions: unSubscribeAll ? [UNSUBSCRIBE_ALL_COMMS] : subscriptions,
        source: getURLParam('source', window.location.href),
        attribution_params: {
          iterable_template_id: getURLParam('iterableTemplateId', window.location.href),
          iterable_campaign_id: getURLParam('iterableCampaignId', window.location.href),
        },
      };
      const response = await put(`/subscriptions/${uuid}`, params);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response;
    },
    onSuccess: (_data, { uuid }) => {
      void queryClient.invalidateQueries({ queryKey: userSubscriptions.fetch({ uuid }).queryKey });
    },
  });
};
